//import Badge from "components/shared/badge";
import Button from "components/shared/button";
import Checkbox from "components/shared/inputs/checkbox";
import Input, { InputType } from "components/shared/inputs/input";
import { Text } from "components/shared/text";
import { SvgPlus } from "components/svg";
import { FC, PropsWithChildren, useState } from "react";
import { Control, FieldValues, useController } from "react-hook-form";
import useTranslation from "utils/hooks/useTranslation";

export type SingleOptionProps =
  | {
    singleOption: true;
    value?: string | number;
    onChange: (value?: string | number) => void;
  }
  | {
    singleOption?: false | never;
    value?: (string | number)[];
    onChange: (value?: (string | number)[]) => void;
  };

export const FilterCheckboxGroup: FC<
  {
    title: string;
    isDefaultExpanded?: boolean;
    options: {
      label: string;
      value: string | number;
      disabled?: boolean;
    }[];
    preventUncheckAll?: boolean;
  } & SingleOptionProps
> = ({
  options,
  title,
  onChange,
  value: valueProp,
  singleOption = false,
  isDefaultExpanded,
  preventUncheckAll,
}) => {
    const value = singleOption ? valueProp : valueProp || [];
    return (
      options?.length > 0 && (
        <FilterGroupContainer
          title={title}
          isDefaultExpanded={isDefaultExpanded}
          valueCount={
            singleOption
              ? options?.filter((o) => value === o.value)?.length
              : (value as (string | number)[])?.length > 0
                ? ((value as (string | number)[])!.length as number)
                : undefined
          }
        >
          {options.map((opt) => {
            const { value: optionValue, label, disabled } = opt;

            return (
              <group
                key={optionValue}
                data-name="filter_item"
                data-interactive=""
              >
                <Checkbox
                  label={label}
                  disabled={disabled}
                  value={
                    (singleOption
                      ? optionValue === value
                      : (value as (number | string)[])?.includes?.(
                        optionValue
                      )) as any
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (singleOption) {
                      onChange((checked ? optionValue : undefined) as any);
                    } else {
                      const newValue = checked
                        ? [optionValue, ...(value as (string | number)[])]
                        : [...(value as (string | number)[])].filter(
                          (v) => v != optionValue
                        );
                      if (preventUncheckAll && !newValue.length) {
                        return;
                      }
                      onChange(
                        newValue.length > 0 ? newValue : (undefined as any)
                      );
                    }
                  }}
                />
              </group>
            );
          })}
        </FilterGroupContainer>
      )
    );
  };

export const FilterInput: FC<{
  title: string;
  name: string;
  control: Control<FieldValues, any>;
  unit?: string;
  isDefaultExpanded?: boolean;
  type?: string;
  min?: number;
  max?: number;
  appliedValue?: any;
  onApply?: () => void;
  onReset?: (field: string) => void;
}> = ({
  title,
  control,
  name,
  unit,
  isDefaultExpanded,
  type,
  onApply,
  onReset,
  appliedValue,
}) => {
    const { t } = useTranslation();
    const { field, fieldState } = useController({ control, name });
    return (
      <FilterGroupContainer
        title={title}
        isDefaultExpanded={isDefaultExpanded}
        valueCount={appliedValue ? 1 : undefined}
      >
        <Input
          dataLength="400"
          control={control}
          type={type || InputType.Text}
          name={name}
          unit={unit && t(unit)}
          button={
            (onApply || onReset) && (
              <group data-length="30">
                {onApply && (fieldState.isDirty || !field.value) && (
                  <Button
                    micro
                    outline
                    onClick={() => onApply()}
                    text="GO"
                    disabled={!!fieldState.error?.message || !fieldState.isDirty}
                  />
                )}
                {onReset && !fieldState.isDirty && !!field.value && (
                  <Button micro onClick={() => onReset(name)}>
                    <icon data-color="main-dark">delete</icon>
                  </Button>
                )}
              </group>
            )
          }
        />
      </FilterGroupContainer>
    );
  };

const FilterGroupContainer: FC<
  PropsWithChildren<{
    title: string;
    isDefaultExpanded?: boolean;
    valueCount?: number;
  }>
> = ({ title, children, isDefaultExpanded = false, valueCount }) => {
  const [isOpen, setIsOpen] = useState(isDefaultExpanded);
  return (
    <group data-name="collapsible" className={isOpen ? "open" : ""}>
      <group
        data-name="collapsible_header"
        data-interactive=""
        data-cursor="pointer"
      >
        <label
          data-type="group"
          data-align="center"
          data-gap="10"
          data-height="40"
          data-space-left="10"
        >
          <group
            data-fit="1"
            data-length="150"
            data-weight="500"
            data-gap="5"
            data-align="center"
          >
            <group data-gap="5" data-align="center">
              <Text data-color="grey-color-dark">{title}</Text>
              {(valueCount as number) > 0 && (
                <group
                  data-width="auto"
                  data-align="center"
                  data-justify="center"
                >
                  <text
                    //data-type="group"
                    data-space-horizontal="5"
                    data-space-vertical="3"
                    //data-line-height="1"
                    data-width="auto"
                    data-background="error-color"
                    data-color="white"
                    data-text-size="10"
                    data-radius="30"
                    data-min-height="10"
                    data-min-length="10"
                    //data-ratio="1:1"

                    data-contain=""
                  >
                    {(valueCount as number) > 1 ? valueCount : undefined}
                  </text>
                </group>
              )}
            </group>
          </group>

          <Button
            mini
            rounded
            className={isOpen ? "open" : ""}
            onClick={() => setIsOpen((prev) => !prev)}
            data-color="grey-color-mid"
          >
            <icon>
              <SvgPlus />
            </icon>
          </Button>
        </label>
      </group>
      <group data-name="collapsible_container">
        <group data-align="center" data-space-vertical="5" data-gap="3">
          {children}
        </group>
      </group>
    </group>
  );
};
